/// <amd-module name="typeahead/core/logging/SuggestionEngagementLogger" />

import {ListItemType} from "typeahead/core/IListItem";
import {CollectorTransport} from "typeahead/core/logging/CollectorTransport";
import {SuggestionEngagementMetric} from "typeahead/core/logging/SuggestionEngagementMetric";
import {ElementFocusedMessage} from "typeahead/core/message/ElementFocusedMessage";
import {EssErrorMessage} from "typeahead/core/message/EssErrorMessage";
import {InputValueEditedMessage} from "typeahead/core/message/InputValueEditedMessage";
import {MoreSuggestionsClickedMessage} from "typeahead/core/message/MoreSuggestionsClickedMessage";
import {SuggestionReadyMessage} from "typeahead/core/message/SuggestionReadyMessage";
import {SuggestionSelectedMessage} from "typeahead/core/message/SuggestionSelectedMessage";
import {SuggestionShownMessage} from "typeahead/core/message/SuggestionShownMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {UserExitedMessage} from "typeahead/core/message/UserExitedMessage";
import {IEssSuggestionResult, ISuggestionResult, Suggestion, SuggestionCategory} from "typeahead/core/Suggestion";
import * as SuggestionFactory from "typeahead/core/SuggestionFactory";
import {Lob, PackageType, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

export class SuggestionEngagementLogger {

    private static areSuggestionsFromGoogle(s: ISuggestionResult[]): boolean {
        if (s && s.length > 0) {
            return SuggestionFactory.createSuggestion(s[0]).isAddrAutoComplete();
        }
        return false;
    }

    private static getSuggestionTypes(s: ISuggestionResult[]): string {
        if (s && s.length > 0) {
            return s.filter((item) => ListItemType.Label !== item.listItemType)
                    .map((item: ISuggestionResult, index: number) =>
              (item.category === SuggestionCategory[SuggestionCategory.GOOGLEMAPS]) ? "GPA" : item.type).join(",");
        }
        return undefined;
    }

    private static getSuggestionIds(s: ISuggestionResult[]): string {
        if (s && s.length > 0) {
            return s
              .filter((item) => ListItemType.Label !== item.listItemType)
              .map((item: ISuggestionResult, index: number) => {
                const suggestion: Suggestion = SuggestionFactory.createSuggestion(item);
                return suggestion.getSourceName().orElse("-") + ":" + suggestion.getSourceId().orElse("-");
            }).join(",");
        }
        return undefined;
    }

    protected lob: string;
    protected domElementId: string;
    protected isSuggestionShown: boolean = false;
    protected isSuggestionSelected: boolean = false;
    protected userTypedQuery: string = "";
    protected selectedSuggestion: Suggestion;
    protected displayText: string = "";
    protected activeESSTests: string[] = [];
    protected areChildrenPresent: boolean = false;
    protected childSuggestionsExpanded: boolean = false;
    protected zeroResults: boolean = false;
    protected error: string;
    protected isAddrAutocomplete: boolean;
    protected suggestionTypes: string;
    protected suggestionIds: string;
    protected interactionTime: number;
    protected totalSuggestion: string;
    protected numberOfPrefixPopCandidates: string;
    private interactionStart: number;

    constructor() {
        // Input element focused
        TypeaheadMessage.subscribe(
            ElementFocusedMessage.getTopic(), (topic: string, message: ElementFocusedMessage) => {
                this.interactionStart = Date.now();
                this.lob = message.getContext().lob;
                this.domElementId = message.getDomElementId();
                this.displayText = message.getElementValue();
                this.error = undefined;
                this.isSuggestionShown = false;
                this.isSuggestionSelected = false;
                this.isAddrAutocomplete = false;
                this.suggestionTypes = undefined;
                this.suggestionIds = undefined;
            },
        );

        TypeaheadMessage.subscribe(
          MoreSuggestionsClickedMessage.getTopic(), (topic: string, message: MoreSuggestionsClickedMessage) => {
              this.childSuggestionsExpanded = true;
            },
        );

        // Suggestions returned from ESS
        TypeaheadMessage.subscribe(
            SuggestionReadyMessage.getTopic(), (topic: string, message: SuggestionReadyMessage) => {
                const elementText: string = $("#" + message.getDomElementId()).val();
                if (!message.getQuery() || message.getQuery() === elementText) {
                    this.zeroResults = (!message.getSuggestions() || message.getSuggestions().length === 0);
                }
            },
        );

        TypeaheadMessage.subscribe(
            EssErrorMessage.getTopic(), (topic: string, message: EssErrorMessage) => {
                this.zeroResults = false;
                this.isSuggestionShown = false;
                this.error = message.getStatusCode();
            },
        );

        // Suggestions displayed
        TypeaheadMessage.subscribe(
            SuggestionShownMessage.getTopic(), (topic: string, message: SuggestionShownMessage) => {
                this.isSuggestionShown = true;
                this.childSuggestionsExpanded = false;
                this.activeESSTests.push(message.getActiveESSTests());
                this.checkForChildren(message.getShownSuggestions());
                this.isAddrAutocomplete =
                  SuggestionEngagementLogger.areSuggestionsFromGoogle(message.getShownSuggestions());
                this.suggestionTypes = SuggestionEngagementLogger.getSuggestionTypes(message.getShownSuggestions());
                this.suggestionIds = SuggestionEngagementLogger.getSuggestionIds(message.getShownSuggestions());
                this.totalSuggestion = message.getTotalSuggestion();
                this.numberOfPrefixPopCandidates = message.getNumberOfPrefixPopCandidates();
            },
        );

        // UserEdit or Suggestion deselected
        TypeaheadMessage.subscribe(
            InputValueEditedMessage.getTopic(), (topic: string, message: InputValueEditedMessage) => {
                this.lob = message.getLob();
                this.domElementId = message.getDomElementId();
                this.isSuggestionSelected = false;
                this.isSuggestionShown = false;
                this.userTypedQuery = message.getNewInputValue();
                this.displayText = message.getNewInputValue();
                this.selectedSuggestion = undefined;
                this.error = undefined;
                this.isAddrAutocomplete = false;
                this.suggestionTypes = undefined;
                this.suggestionIds = undefined;
            },
        );

        // Suggestion selected
        TypeaheadMessage.subscribe(
            SuggestionSelectedMessage.getTopic(),
            (topic: string, message: SuggestionSelectedMessage) => {
                this.lob = message.getLob();
                this.selectedSuggestion = message.getSuggestion();
            },
        );

        TypeaheadMessage.subscribe(
            UserExitedMessage.getTopic(), () => {
                if (this.domElementId) {
                    this.lob =
                        ($("#" + this.domElementId).attr("data-mask") === "4096") ? "DISCOVER" : this.lob;
                    this.interactionTime = Date.now() - this.interactionStart;
                    this.emitMetric();

                    // Reset logger state
                    this.domElementId = undefined;
                    this.lob = undefined;
                    this.isSuggestionShown = false;
                    this.isSuggestionSelected = false;
                    this.userTypedQuery = "";
                    this.displayText = "";
                    this.selectedSuggestion = undefined;
                    this.activeESSTests = [];
                }
            },
        );
    }

    protected updatePackageType(): void {
        const getPackageTypeString = (pt: PackageType): string => {
            switch (pt) {
                case PackageType.FlightHotelCar:
                    return "FHC";
                case PackageType.HotelCar:
                    return "HC";
                case PackageType.FlightCar:
                    return "FC";
                case PackageType.FlightHotel:
                    return "FH";
                default:
                    return "-";
            }
        };

        const packageType: PackageType = TypeaheadUtils.getPackageType();
        if (packageType !== PackageType.Unknown) {
            this.lob = "PACKAGES:" + getPackageTypeString(packageType);
        }
    }

    protected checkForChildren(results: IEssSuggestionResult[]): void {
        const lob = TypeaheadUtils.getLob();
        this.areChildrenPresent = results.some((result) =>
            lob === Lob.Flight ? TypeaheadUtils.isNearBySuggestion(result) : TypeaheadUtils.isChildSuggestion(result));
    }

    protected removeDuplicateESSTests(): void {
        let tempTests: string[] = [];
        for (const tests of this.activeESSTests) {
            if (typeof tests === "string" && tests.length > 0) {
                    tempTests = tempTests.concat(tests.split("|"));
            }
        }
        tempTests = tempTests.sort()
            .filter( (item, pos, ary): boolean => {
                    return !pos || item !== ary[pos - 1];
            });
        while (tempTests.indexOf("") === 0) {
            tempTests.shift();
        }
        this.activeESSTests = tempTests;
    }

    protected emitMetric(): void {
        this.updatePackageType();
        this.removeDuplicateESSTests();
        new CollectorTransport().log(new SuggestionEngagementMetric(
            this.lob,
            this.domElementId,
            this.userTypedQuery,
            this.displayText,
            this.isSuggestionShown,
            this.selectedSuggestion,
            this.activeESSTests.join("|")), "typeahead_client_metrics");
    }
}
